import type { CodeableConcept } from '@medplum/fhirtypes'
import { omit, pick } from 'lodash-es'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { CARE_TEAM_CATEGORY } from '#src/codeSystems.ts'
import { ROLES, Role } from '#src/roles.ts'
import { recordEntries, recordValues } from '#src/utils.ts'

export const CARE_TEAM_CATEGORIES = {
  IDT: codeableConcept({
    system: CARE_TEAM_CATEGORY,
    code: 'idt',
    display: 'Interdisciplinary Team',
  }),
}

export const Team = {
  IDT: 'IDT',
  TECH: 'TECH',
  MOE: 'MOE',
} as const
export type Team = (typeof Team)[keyof typeof Team]

export const moe = [
  Role.OUTREACH_SPECIALIST,
  Role.OUTREACH_ENROLLMENT_COORDINATOR,
  Role.INTAKE_COORDINATOR,
]

export const operations = [
  Role.FACILITIES,
  Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
  Role.QI_COMP_DIRECTOR,
]

export const assessmentDay1Roles = [Role.PCP, Role.RN, Role.SW] as const

export const assessmentDay2Roles = [Role.PT, Role.OT, Role.RT, Role.RD] as const

export const idtRoles = [
  Role.PCP,
  Role.RN,
  Role.SW,
  Role.PT,
  Role.OT,
  Role.RT,
  Role.RD,
  Role.CM,
  Role.HC,
  Role.PC,
  Role.TR,
] as const
export type IDTRole = (typeof idtRoles)[number]

export const clinical = [Role.RN, Role.LVN, Role.PCP, Role.NP, Role.MA, Role.MD]

export const schedulableRoles = [
  Role.PCP,
  Role.RN,
  Role.SW,
  Role.PT,
  Role.OT,
  Role.RT,
  Role.RD,
  Role.CM,
  Role.HC,
  Role.PC,
  Role.MA,
  Role.NP,
  Role.LVN,
  Role.PARTICIPANT_EXPERIENCE_SUPERVISOR,
  Role.CNA,
  Role.PTA,
]

export type SchedulableRole = (typeof schedulableRoles)[number]
export function isRole(code: string): code is Role {
  return recordValues(Role).includes(code as Role)
}

export function isIDTRole(code: string): code is IDTRole {
  return idtRoles.includes(code as IDTRole)
}

export function getRoleConcepts(roles: Role[]): CodeableConcept[] {
  return recordValues(pick(ROLES, roles))
}

export const IDT_ROLE_OPTIONS_WITH_KEYWORDS = recordEntries(
  pick(ROLES, idtRoles)
).map(([_, value]) => ({
  value: codeFromCodeableConcept(value) as IDTRole,
  label: displayFromCodeableConcept(value) as string,
  keywords: [codeFromCodeableConcept(value)],
}))

export const IDT_ROLE_OPTIONS = IDT_ROLE_OPTIONS_WITH_KEYWORDS.map((option) =>
  omit(option, 'keywords')
)

export const ROLE_OPTIONS_WITH_KEYWORDS = recordEntries(ROLES).map(
  ([_, value]) => ({
    value: codeFromCodeableConcept(value) ?? '',
    label: displayFromCodeableConcept(value) ?? '',
    keywords: [
      codeFromCodeableConcept(value) ?? '',
      displayFromCodeableConcept(value) ?? '',
    ],
  })
)

export const ROLE_OPTIONS = ROLE_OPTIONS_WITH_KEYWORDS.map((option) =>
  omit(option, 'keywords')
)
